import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ProfileDetailsInputProps {
  skills: string;
  setSkills: (skills: string) => void;
  experience: string;
  setExperience: (experience: string) => void;
  title: string;
  setTitle: (title: string) => void;
  targetAudience: string;
  setTargetAudience: (targetAudience: string) => void;
}

const ProfileDetailsInput: React.FC<ProfileDetailsInputProps> = ({
  skills,
  setSkills,
  experience,
  setExperience,
  title,
  setTitle,
  targetAudience,
  setTargetAudience,
}) => {
  const renderTooltip = (content: string) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger type="button">
          <Info className="h-4 w-4 ml-2 text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[300px] p-4">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="title" className="text-base font-medium">
            Professional Title
          </Label>
          {renderTooltip("Examples: 'Full-Stack Developer specializing in SaaS' or 'Senior UI/UX Designer with 8 years of experience'")}
        </div>
        <Input
          id="title"
          placeholder="E.g., Full-Stack Developer | React & Node.js Expert | 7+ Years Experience"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-12"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="skills" className="text-base font-medium">
            Skills & Technologies*
          </Label>
          {renderTooltip("List your technical skills, tools, and technologies. Be specific with versions or years of experience with each if relevant.")}
        </div>
        <Textarea
          id="skills"
          placeholder="React.js, Node.js, Express, MongoDB, AWS, Docker, TypeScript, Redux, GraphQL, REST APIs, React Native, etc."
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          className="min-h-[100px] resize-y"
          required
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="experience" className="text-base font-medium">
            Experience & Achievements*
          </Label>
          {renderTooltip("Include years of experience, previous projects, quantifiable achievements (e.g., 'Increased site speed by 40%'), and industries you've worked with.")}
        </div>
        <Textarea
          id="experience"
          placeholder="8+ years of experience in web development. Built 50+ responsive websites and applications. Created a real-time dashboard that increased team efficiency by 25%. Worked with clients in healthcare, e-commerce, and fintech sectors."
          value={experience}
          onChange={(e) => setExperience(e.target.value)}
          className="min-h-[150px] resize-y"
          required
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="targetAudience" className="text-base font-medium">
            Target Clients & Industries
          </Label>
          {renderTooltip("Specify the types of clients you want to attract and industries you prefer to work in.")}
        </div>
        <Textarea
          id="targetAudience"
          placeholder="E.g., SaaS startups, e-commerce businesses, healthcare organizations, enterprise clients needing legacy system updates"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          className="min-h-[100px] resize-y"
        />
      </div>
      
      <div className="text-sm text-muted-foreground bg-muted/20 p-3 rounded-md border border-muted/30">
        <p className="font-medium mb-1">Tips for better results:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Be specific with your skills (e.g., "React with Redux and TypeScript" instead of just "JavaScript")</li>
          <li>Include measurable achievements with numbers when possible</li>
          <li>Mention years of experience with specific technologies</li>
          <li>List industries you've worked in if relevant</li>
        </ul>
      </div>
    </div>
  );
};

export default ProfileDetailsInput;
